import { useEffect, useMemo, useState } from 'react';
import type { Member } from '@catalyst-psa/shared';
import { DEFAULT_PAGE_SIZE } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { useRoles } from '../roles/api.js';
import { MemberFormModal } from './MemberFormModal.js';
import { useMembers } from './api.js';
import { formatDateTime } from '../../utils/date.js';

/** A member counts as "online now" when last seen within this window. */
const ONLINE_WINDOW_MS = 5 * 60_000;

/** True when the member was active within the online window. */
function isOnline(lastSeenAt: string | null): boolean {
  if (!lastSeenAt) return false;
  const seen = new Date(lastSeenAt).getTime();
  return Number.isFinite(seen) && Date.now() - seen < ONLINE_WINDOW_MS;
}

export function MembersPage(): JSX.Element {
  const { isAdmin } = useAuth();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | ''>('active');
  const [roleId, setRoleId] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);

  const query = useMembers(
    {
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      isActive: statusFilter === '' ? undefined : statusFilter === 'active',
      roleId: roleId || undefined,
    },
    // Keep lastSeenAt fresh so the Online column tracks reality while the page is open.
    { refetchInterval: 30_000 },
  );
  const rolesQuery = useRoles({ page: 1, pageSize: 200 });

  // Re-render periodically so Online badges also decay between refetches.
  const [, setOnlineTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setOnlineTick((n) => n + 1), 30_000);
    return () => clearInterval(timer);
  }, []);

  const roleNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const role of rolesQuery.data?.data ?? []) {
      map.set(role.id, role.name);
    }
    return map;
  }, [rolesQuery.data]);

  const members = query.data?.data ?? [];
  const pageInfo = query.data?.page;

  const openCreate = (): void => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (member: Member): void => {
    setEditing(member);
    setFormOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Members"
        description="Manage people who sign in with Entra ID and the roles they hold."
        actions={
          isAdmin ? (
            <Button onClick={openCreate}>
              <Icon name="members" className="h-4 w-4" />
              New member
            </Button>
          ) : undefined
        }
      />

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 p-4">
          <select
            value={statusFilter}
            onChange={(e) => {
              setPage(1);
              setStatusFilter(e.target.value as 'active' | 'inactive' | '');
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={roleId}
            onChange={(e) => {
              setPage(1);
              setRoleId(e.target.value);
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All roles</option>
            {(rolesQuery.data?.data ?? []).map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </select>
        </div>
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Roles</th>
              <th className="px-4 py-3 font-medium">Last login</th>
              <th className="px-4 py-3 font-medium">Online</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            <DataState
              colSpan={6}
              isLoading={query.isLoading}
              isError={query.isError}
              error={query.error}
              onRetry={() => void query.refetch()}
              isEmpty={members.length === 0}
              emptyTitle="No members found"
              emptyDescription="Add your first member to get started."
              emptyAction={isAdmin ? <Button onClick={openCreate}>New member</Button> : undefined}
            >
              {members.map((member) => (
                <tr
                  key={member.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => openEdit(member)}
                >
                  <td className="px-4 py-3 font-medium text-slate-900">{member.displayName}</td>
                  <td className="px-4 py-3 text-slate-600">{member.email}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {member.roleIds.length === 0
                      ? '—'
                      : member.roleIds.map((id) => roleNames.get(id) ?? '…').join(', ')}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {formatDateTime(member.lastLoginAt)}
                  </td>
                  <td className="px-4 py-3">
                    {isOnline(member.lastSeenAt) ? (
                      <Badge tone="green">Online</Badge>
                    ) : (
                      <span
                        className="text-slate-400"
                        title={member.lastSeenAt ? `Last seen ${formatDateTime(member.lastSeenAt)}` : undefined}
                      >
                        Offline
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={member.isActive ? 'green' : 'gray'}>
                      {member.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </DataState>
          </tbody>
        </table>

        {pageInfo && members.length > 0 ? (
          <Pagination
            page={pageInfo.page}
            pageSize={pageInfo.pageSize}
            totalItems={pageInfo.totalItems}
            totalPages={pageInfo.totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <MemberFormModal isOpen={formOpen} member={editing} onClose={() => setFormOpen(false)} />
    </div>
  );
}
